import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { SonaAnalytics } from '@sona/angular-util-analytics';
import { useLanguage } from '@sona/angular-util-common';

import { SUPABASE } from '../../tokens/supabase.token';
import { AuthState } from '../auth.state';

/**
 * A facade for the login feature
 */
@Injectable({
    providedIn: 'root'
})
export class SonaAuthLoginFacade {

    /**
     * Whether the user is authenticated
     *
     * @param state
     * @param session
     */
    static isAuthenticated = AuthState.isAuthenticated;

    private readonly _supabase = inject(SUPABASE);
    private readonly _store = inject(Store);
    private readonly _analytics = inject(SonaAnalytics);

    readonly language = useLanguage();

    /**
     * Triggers the login with Google flow
     */
    signInWithGoogle() {
        void this._analytics.track('sign-in', {
            method: 'oauth',
            provider: 'google'
        });

        return this._supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: location.origin
            }
        });
    }

    /**
     * Triggers the One Time Password (OTP) login flow
     */
    signInWithOtp(email: string) {
        void this._analytics.track('sign-in', {
            method: 'otp'
        });

        return this._supabase.auth.signInWithOtp({
            email,
            options: {
                shouldCreateUser: true,
                data: { lang: this.language() }
            }
        });
    }

    signInWithPassword(email: string, password: string) {
        void this._analytics.track('sign-in', {
            method: 'password'
        });

        return this._supabase.auth.signInWithPassword({
            email,
            password
        });
    }

    resetPassword(email: string) {
        void this._analytics.track('reset-password', {
            email
        });

        return this._supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${location.origin}/auth/change-password`
        });
    }
}
